<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-row class="mb-1">
          <b-col
            cols="12"
            md="7"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              variant="outline-primary"
              class="btn-icon"
              :to="{ name: 'events' }"
              ><i class="fa-solid fa-arrow-left"></i
              ><span class="ml-25">Back</span></b-button
            >
            <h3 class="ml-1">Edit Event</h3>
          </b-col>
        </b-row>

        <b-form v-if="loaded" @submit.prevent="onSubmit">
          <b-row>
            <b-col cols="12" md="8">
              <!-- General Section -->
              <div class="section-card mb-1">
                <label>Course *</label>
                <v-select
                  class="flex-grow-1 mb-1"
                  :options="options.courses"
                  label="title"
                  :reduce="(c) => c.id"
                  v-model="form.course_id"
                  required
                />

                <b-row>
                  <b-col md="4"
                    ><b-form-group label="Year *"
                      ><b-form-input
                        type="number"
                        v-model.number="form.year"
                        min="2000"
                        required/></b-form-group
                  ></b-col>
                  <b-col md="4"
                    ><b-form-group label="Time zone *"
                      ><b-form-input
                        v-model="form.time_zone"
                        required/></b-form-group
                  ></b-col>
                  <b-col md="4"
                    ><b-form-group label="Status"
                      ><v-select
                        :options="options.enums.event_statuses"
                        v-model="form.status"
                        :reduce="(v) => v"/></b-form-group
                  ></b-col>
                </b-row>
              </div>

              <!-- Parts Section -->
              <div class="parts-section mb-1">
                <div class="d-flex justify-content-between align-items-center parts-header">
                  <h5 class="mb-0">Parts</h5>
                  <b-button size="sm" variant="primary" @click="addSession">+ Add part</b-button>
                </div>

                <div
                  v-for="(s, idx) in sessions"
                  :key="s.id ? s.id : idx"
                  class="session-card-edit mb-1"
                >
                  <div class="d-flex justify-content-between align-items-center session-edit-header">
                    <h6 class="mb-0">{{ getPartTitle(idx) }}</h6>
                    <div>
                      <b-button
                        size="sm"
                        class="mr-50"
                        variant="outline-primary"
                        @click="saveSession(s)"
                        >Save</b-button
                      >
                      <b-button
                        size="sm"
                        class="mr-50"
                        variant="outline-secondary"
                        v-if="canManageAttendance() && s.id"
                        :to="{
                          name: 'event-attendance',
                          params: { sessionId: s.id },
                        }"
                        :disabled="!s.id"
                        >Attendance</b-button
                      >
                      <b-button
                        size="sm"
                        variant="outline-danger"
                        v-if="sessions.length > 1"
                        @click="deleteSession(s)"
                        >Remove</b-button
                      >
                    </div>
                  </div>
                  <b-row class="mt-50">
                    <b-col md="4"
                      ><label>Start</label
                      ><b-form-input type="datetime-local" v-model="s.start_at"
                    /></b-col>
                    <b-col md="4"
                      ><label>End</label
                      ><b-form-input
                        type="datetime-local"
                        v-model="s.end_at"
                        :min="s.start_at"
                    /></b-col>
                    <b-col md="4">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <label>Venue</label>
                        <a
                          href="#"
                          @click.prevent="openAddVenueModal(idx)"
                          class="text-primary text-decoration-underline"
                          style="font-size: 0.875rem;"
                          >Add Venue</a
                        >
                      </div>
                      <v-select
                        :options="options.venues"
                        label="name"
                        :reduce="(v) => v.id"
                        v-model="s.venue_id"
                      />
                    </b-col>
                  </b-row>
                  <b-row class="mt-50">
                    <b-col md="6"
                      ><label>Meeting URL</label
                      ><b-form-input v-model="s.meeting_url"
                    /></b-col>
                    <b-col md="3"
                      ><label>Passcode</label
                      ><b-form-input v-model="s.meeting_passcode"
                    /></b-col>
                    <b-col md="3">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <label>Trainer</label>
                        <a
                          href="#"
                          @click.prevent="openAddTrainerModal(idx)"
                          class="text-primary text-decoration-underline"
                          style="font-size: 0.875rem;"
                          >Add Trainer</a
                        >
                      </div>
                      <v-select
                        :options="options.trainers"
                        label="name"
                        :reduce="(t) => t.id"
                        v-model="s.primary_trainer_id"
                      />
                    </b-col>
                  </b-row>
                </div>
              </div>

              <!-- Applications & Details Section -->
              <div class="section-card mb-1">
                <b-row>
                  <b-col md="6"
                    ><b-form-group label="Application method"
                      ><v-select
                        :options="options.enums.application_methods"
                        v-model="form.application_method"
                        :reduce="(v) => v"
                        @input="onApplicationMethodChange"/></b-form-group
                  ></b-col>
                  <b-col md="6"
                    ><b-form-group label="Application deadline"
                      ><b-form-input
                        type="date"
                        v-model="form.application_deadline"/></b-form-group
                  ></b-col>
                </b-row>

                <!-- Conditional application description field -->
                <b-row v-if="showApplicationDescription">
                  <b-col cols="12">
                    <b-form-group :label="applicationDescriptionLabel">
                      <b-form-input
                        v-if="isEmailApplication"
                        type="email"
                        v-model="form.application_description"
                        :placeholder="applicationDescriptionPlaceholder"
                        required
                      />
                      <b-form-input
                        v-else-if="isExternalApplication"
                        type="text"
                        v-model="form.application_description"
                        :placeholder="applicationDescriptionPlaceholder"
                        required
                      />
                      <b-form-textarea
                        v-else
                        rows="2"
                        v-model="form.application_description"
                        :placeholder="applicationDescriptionPlaceholder"
                        required
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col md="4"
                    ><b-form-group label="Max participants"
                      ><b-form-input
                        type="number"
                        min="1"
                        v-model.number="form.max_participants"/></b-form-group
                  ></b-col>
                  <b-col md="4" class="d-flex align-items-center"
                    ><b-form-checkbox v-model="form.is_pre_approved"
                      >If pre-approved event</b-form-checkbox
                    ></b-col
                  >
                  <b-col md="4" class="d-flex align-items-center"
                    ><b-form-checkbox v-model="form.require_manager_approval"
                      >Require manager approval</b-form-checkbox
                    ></b-col
                  >
                </b-row>

                <b-form-group label="Title *"
                  ><b-form-input v-model="form.title_override" required
                /></b-form-group>
                <b-form-group label="Description"
                  ><b-form-textarea v-model="form.description_override" rows="2"
                /></b-form-group>
              </div>

              <!-- Target Audience Section -->
              <div class="section-card mb-1">
                <h5 class="mb-50">Target Audience</h5>
                <b-row>
                  <b-col md="4">
                    <b-form-group>
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <label>Companies</label>
                        <div class="text-right">
                          <a
                            v-if="form.company_ids.length === 0"
                            href="#"
                            @click.prevent="selectAllCompanies"
                            class="text-primary text-decoration-underline"
                            style="font-size: 0.875rem;"
                          >
                            Select All
                          </a>
                          <a
                            v-else
                            href="#"
                            @click.prevent="clearAllCompanies"
                            class="text-danger text-decoration-underline"
                            style="font-size: 0.875rem;"
                          >
                            Clear All
                          </a>
                        </div>
                      </div>
                      <v-select
                        multiple
                        :options="options.companies"
                        label="title"
                        :reduce="(c) => c.id"
                        v-model="form.company_ids"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="4">
                    <b-form-group>
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <label>Working Groups</label>
                        <div class="text-right">
                          <a
                            v-if="form.working_group_ids.length === 0"
                            href="#"
                            @click.prevent="selectAllWorkingGroups"
                            class="text-primary text-decoration-underline"
                            style="font-size: 0.875rem;"
                          >
                            Select All
                          </a>
                          <a
                            v-else
                            href="#"
                            @click.prevent="clearAllWorkingGroups"
                            class="text-danger text-decoration-underline"
                            style="font-size: 0.875rem;"
                          >
                            Clear All
                          </a>
                        </div>
                      </div>
                      <v-select
                        multiple
                        :options="options.workingGroups"
                        label="group_name"
                        :reduce="(wg) => wg.id"
                        v-model="form.working_group_ids"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="4">
                    <b-form-group>
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <label>Roles</label>
                        <div class="text-right">
                          <a
                            v-if="form.role_ids.length === 0"
                            href="#"
                            @click.prevent="selectAllRoles"
                            class="text-primary text-decoration-underline"
                            style="font-size: 0.875rem;"
                          >
                            Select All
                          </a>
                          <a
                            v-else
                            href="#"
                            @click.prevent="clearAllRoles"
                            class="text-danger text-decoration-underline"
                            style="font-size: 0.875rem;"
                          >
                            Clear All
                          </a>
                        </div>
                      </div>
                      <v-select
                        multiple
                        :options="options.roles"
                        label="name"
                        :reduce="(r) => r.id"
                        v-model="form.role_ids"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>

              <!-- Event Reminders Section -->
              <div class="section-card mb-1">
                <h5 class="mb-25">Event Reminders</h5>
                <p class="text-muted small">
                  Set up automatic reminders to be sent to participants before the
                  event starts.
                </p>

                <div
                  v-for="(reminder, idx) in reminders"
                  :key="`reminder-${idx}`"
                  class="border rounded p-1 mb-1"
                >
                  <div
                    class="d-flex justify-content-between align-items-center mb-2"
                  >
                    <h6 class="mb-0">Reminder {{ idx + 1 }}</h6>
                    <b-button
                      size="sm"
                      variant="outline-danger"
                      @click="removeReminder(idx)"
                      >Remove</b-button
                    >
                  </div>
                  <b-row>
                    <b-col md="4">
                      <label>Type *</label>
                      <v-select
                        :options="reminderTypeOptions"
                        label="label"
                        v-model="reminder.type"
                        :reduce="(opt) => opt.value"
                      />
                    </b-col>
                    <b-col md="4">
                      <label>Days Before Event *</label>
                      <b-form-input
                        type="number"
                        v-model.number="reminder.days_before"
                        min="1"
                        max="30"
                        placeholder="e.g. 3"
                        required
                      />
                    </b-col>
                    <b-col md="8">
                      <label>Custom Message (Optional)</label>
                      <b-form-input
                        v-model="reminder.message"
                        placeholder="Custom reminder message..."
                      />
                    </b-col>
                  </b-row>
                </div>

                <b-button size="sm" variant="outline-primary" @click="addReminder"
                  >+ Add Reminder</b-button
                >
              </div>

              <!-- Actions -->
              <div class="mt-2">
                <b-button type="submit" variant="primary">Update</b-button>
                <b-button
                  variant="outline-secondary"
                  class="ml-1"
                  :to="{ name: 'events' }"
                  >Cancel</b-button
                >
              </div>
            </b-col>

            <b-col cols="12" md="4">
              <b-card title="Event Coordinators">
                <v-select
                  multiple
                  :options="options.users"
                  label="title"
                  :reduce="(u) => u.id"
                  v-model="admin_user_ids"
                  @input="saveAdmins"
                />
              </b-card>

              <b-card class="mt-1" title="Preview">
                <div>
                  <div class="mb-25">
                    <strong>Course:</strong>
                    {{ (selectedCourse && selectedCourse.title) || "-" }}
                  </div>
                  <div class="mb-25">
                    <strong>Aim:</strong>
                    {{ (selectedCourse && selectedCourse.aim) || "-" }}
                  </div>
                  <div class="mb-25">
                    <strong>Learning outcomes:</strong>
                    <ul class="mb-0">
                      <li
                        v-for="(lo, i) in (selectedCourse &&
                          selectedCourse.learning_outcomes) ||
                          []"
                        :key="i"
                      >
                        {{ lo }}
                      </li>
                    </ul>
                  </div>
                </div>
              </b-card>

              <b-card class="mt-1" title="Materials">
                <b-table
                  small
                  responsive
                  bordered
                  :items="materials"
                  :fields="materialsFields"
                  show-empty
                  empty-text="No materials"
                >
                  <template #cell(title)="{ item }">
                    <div v-if="editingId !== item.id">{{ item.title }}</div>
                    <b-form-input v-else v-model="editRow.title" />
                  </template>
                  <template #cell(link)="{ item }">
                    <div v-if="item.external_url">
                      <a :href="item.external_url" target="_blank">External</a>
                    </div>
                    <div v-else-if="item.file_path">
                      <a :href="materialFileHref(item.file_path)" target="_blank">Download</a>
                    </div>
                    <div v-else>-</div>
                    <div v-if="editingId === item.id" class="mt-50">
                      <b-form-input
                        v-model="editRow.external_url"
                        placeholder="External URL"
                        class="mb-50"
                      />
                      <b-form-file
                        v-model="editRow.file"
                        browse-text="Choose file"
                        placeholder="No file chosen"
                      />
                    </div>
                  </template>
                  <template #cell(visible)="{ item }">
                    <div v-if="editingId !== item.id">
                      {{
                        item.visible_from ? dateFormat(item.visible_from) : "-"
                      }}
                      to
                      {{ item.visible_to ? dateFormat(item.visible_to) : "-" }}
                    </div>
                    <div v-else>
                      <b-form-input
                        type="date"
                        v-model="editRow.visible_from"
                        class="mb-25"
                      />
                      <b-form-input type="date" v-model="editRow.visible_to" :min="editRow.visible_from" />
                    </div>
                  </template>
                  <template #cell(accepted)="{ item }">
                    <div v-if="editingId !== item.id">
                      <b-badge
                        :variant="
                          item.is_visible_to_accepted_only
                            ? 'light-info'
                            : 'light-secondary'
                        "
                        >{{
                          item.is_visible_to_accepted_only ? "Yes" : "No"
                        }}</b-badge
                      >
                    </div>
                    <div v-else>
                      <b-form-checkbox
                        v-model="editRow.is_visible_to_accepted_only"
                        >Accepted only</b-form-checkbox
                      >
                    </div>
                  </template>
                  <template #cell(course)="{ item }">{{
                    (item.course && item.course.title) || "-"
                  }}</template>
                  <template #cell(actions)="{ item }">
                    <div v-if="canEditEvent()">
                      <div v-if="editingId !== item.id">
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="mr-25"
                          @click="editMaterialStart(item)"
                          >Edit</b-button
                        >
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          @click="deleteMaterial(item)"
                          >Delete</b-button
                        >
                      </div>
                      <div v-else>
                        <b-button
                          size="sm"
                          variant="primary"
                          class="mr-25"
                          @click="editMaterialSave(item)"
                          >Save</b-button
                        >
                        <b-button
                          size="sm"
                          variant="outline-secondary"
                          @click="editMaterialCancel"
                          >Cancel</b-button
                        >
                      </div>
                    </div>
                    <div v-else>-</div>
                  </template>
                </b-table>

                <hr />
                <h6 v-if="canEditEvent()">
                  Add new material
                </h6>
                <b-form v-if="canEditEvent()" @submit.prevent="uploadMaterial">
                  <b-form-group label="Title"
                    ><b-form-input v-model="materialForm.title" required
                  /></b-form-group>
                  <!-- <b-form-group label="Course (optional)"
                    ><v-select
                      :options="options.courses"
                      label="title"
                      :reduce="(c) => c.id"
                      v-model="materialForm.course_id"
                  /></b-form-group> -->
                  <b-form-group label="External URL (optional)"
                    ><b-form-input
                      v-model="materialForm.external_url"
                      placeholder="https://..."
                  /></b-form-group>
                  <b-form-group label="File (optional)"
                    ><b-form-file
                      v-model="materialForm.file"
                      browse-text="Choose file"
                      placeholder="No file chosen"
                  /></b-form-group>
                  <b-row>
                    <b-col md="6"
                      ><b-form-group label="Visible from"
                        ><b-form-input
                          type="date"
                          v-model="materialForm.visible_from"/></b-form-group
                    ></b-col>
                    <b-col md="6"
                      ><b-form-group label="Visible to"
                        ><b-form-input
                          type="date"
                          v-model="materialForm.visible_to"/></b-form-group
                    ></b-col>
                  </b-row>
                  <b-form-checkbox
                    v-model="materialForm.is_visible_to_accepted_only"
                    >Visible to accepted applicants only</b-form-checkbox
                  >
                  <div class="mt-1">
                    <b-button type="submit" variant="primary">Upload</b-button>
                  </div>
                </b-form>
              </b-card>
            </b-col>
          </b-row>
        </b-form>
      </b-overlay>
    </b-card>

    <!-- Add Venue Modal -->
    <b-modal
      v-model="showAddVenueModal"
      title="Add New Venue"
      @ok="handleAddVenue"
      @cancel="resetVenueForm"
      ok-title="Add"
      cancel-title="Cancel"
    >
      <b-form-group label="Name *" label-for="venue-name">
        <b-form-input
          id="venue-name"
          v-model="newVenue.name"
          placeholder="Enter venue name"
          required
        />
      </b-form-group>
    </b-modal>

    <!-- Add Trainer Modal -->
    <b-modal
      v-model="showAddTrainerModal"
      title="Add New Trainer"
      @ok="handleAddTrainer"
      @cancel="resetTrainerForm"
      ok-title="Add"
      cancel-title="Cancel"
    >
      <b-form-group label="Name *" label-for="trainer-name">
        <b-form-input
          id="trainer-name"
          v-model="newTrainer.name"
          placeholder="Enter trainer name"
          required
        />
      </b-form-group>
      <b-form-group label="Email *" label-for="trainer-email">
        <b-form-input
          id="trainer-email"
          v-model="newTrainer.email"
          type="email"
          placeholder="Enter trainer email"
          pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
          required
        />
      </b-form-group>
      <b-form-group label="Phone" label-for="trainer-phone">
        <b-form-input
          id="trainer-phone"
          v-model="newTrainer.phone"
          placeholder="Enter trainer phone"
        />
      </b-form-group>
      <b-form-group label="CV (PDF)" label-for="trainer-cv">
        <b-form-file
          id="trainer-cv"
          v-model="newTrainerCV"
          accept=".pdf"
          placeholder="Choose a PDF file or drop it here..."
          drop-placeholder="Drop PDF file here..."
        />
      </b-form-group>
    </b-modal>
  </div>
</template>
<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BTable,
  BFormFile,
  BBadge,
  BModal,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import eventsStoreModule from "./eventsStoreModule";
import settingsStoreModule from "@/views/settings/settingsStoreModule";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BTable,
    BFormFile,
    BBadge,
    BModal,
    vSelect,
    ToastificationContent,
  },
  data() {
    return {
      loading: false,
      loaded: false,
      options: {
        courses: [],
        users: [],
        trainers: [],
        venues: [],
        companies: [],
        workingGroups: [],
        enums: { event_statuses: [], application_methods: [] },
      },
      form: {
        id: null,
        course_id: null,
        year: new Date().getFullYear(),
        title_override: "",
        description_override: "",
        status: "draft",
        max_participants: null,
        application_method: null,
        application_deadline: null,
        application_description: "",
        require_manager_approval: false,
        is_pre_approved: false,
        time_zone: "Europe/Dublin",
        notes_admin: "",
        company_ids: [],
        working_group_ids: [],
        role_ids: [],
      },
      sessions: [],
      admin_user_ids: [],
      reminders: [], // Event reminders
      reminderTypeOptions: [
        { label: "Event Reminder", value: "event_reminder" },
        { label: "Application Reminder", value: "application_reminder" },
      ],
      selectedCourse: null,
      // Modal states
      showAddVenueModal: false,
      showAddTrainerModal: false,
      currentSessionIndex: null, // Track which session is being edited
      // New item forms
      newVenue: {
        name: "",
      },
      newTrainer: {
        name: "",
        email: "",
        phone: "",
      },
      newTrainerCV: null,
      materials: [],
      materialsFields: [
        { key: "title", label: "Title", thStyle: "min-width: 200px;" },
        { key: "link", label: "Link", thStyle: "min-width: 150px;" },
        { key: "visible", label: "Visible", thStyle: "min-width: 200px;" },
        {
          key: "accepted",
          label: "Accepted only",
          thStyle: "min-width: 120px;",
        },
        { key: "course", label: "Course", thStyle: "min-width: 150px;" },
        { key: "actions", label: "Actions", thStyle: "min-width: 150px;" },
      ],
      materialForm: {
        title: "",
        file: null,
        external_url: "",
        visible_from: "",
        visible_to: "",
        is_visible_to_accepted_only: true,
        course_id: null,
      },
      editingId: null,
      editRow: {},
      applicationDescriptionLabel: "",
      applicationDescriptionPlaceholder: "",
      showApplicationDescription: false,
    };
  },
  setup() {
    const MODULE = "events";
    if (!store.hasModule(MODULE))
      store.registerModule(MODULE, eventsStoreModule);

    const SETTINGS_MODULE = "settings";
    if (!store.hasModule(SETTINGS_MODULE))
      store.registerModule(SETTINGS_MODULE, settingsStoreModule);

    onUnmounted(() => {
      if (store.hasModule(MODULE)) store.unregisterModule(MODULE);
      if (store.hasModule(SETTINGS_MODULE))
        store.unregisterModule(SETTINGS_MODULE);
    });
  },
  computed: {
    materialsURL() {
      return this.$store.state.app.materialsURL;
    },
  },
  async created() {
    await this.loadOptions();
    this.loadEvent();
  },
  watch: {
    'form.course_id'(newVal, oldVal) {
      // When course changes, fetch course and populate title/description if empty
      if (!newVal) return;
      store.dispatch('events/getCourseById', { id: newVal }).then(res => {
        const course = res.data;
        this.selectedCourse = course;
        if (!this.form.title_override || this.form.title_override.trim() === '') {
          this.form.title_override = course.title || '';
        }
        if (!this.form.description_override || this.form.description_override.trim() === '') {
          this.form.description_override = course.summary || '';
        }
      }).catch(() => {});
    }
  },
  methods: {
    canEditEvent() {
      return this.$CanEvent('edit_event', this.form.admins || this.form || []);
    },
    canManageAttendance() {
      return this.$CanEvent('manage_event_attendance', this.form.admins || this.form || []);
    },
    dateFormat(val) {
      if (!val) return "-";
      const date = new Date(val);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const time = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${day}-${month}-${year} ${time}`;
    },
    async loadOptions() {
      try {
        const res = await store.dispatch("events/fetchEventOptions");
        this.options = res.data;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Failed to load options",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    
    // Select All / Clear All methods for Companies
    selectAllCompanies() {
      this.form.company_ids = this.options.companies.map((c) => c.id);
    },
    clearAllCompanies() {
      this.form.company_ids = [];
    },
    // Select All / Clear All methods for Working Groups
    selectAllWorkingGroups() {
      this.form.working_group_ids = this.options.workingGroups.map(
        (wg) => wg.id
      );
    },
    clearAllWorkingGroups() {
      this.form.working_group_ids = [];
    },
    // Select All / Clear All methods for Roles
    selectAllRoles() {
      this.form.role_ids = this.options.roles.map((r) => r.id);
    },
    clearAllRoles() {
      this.form.role_ids = [];
    },
    async loadEvent() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        const res = await store.dispatch("events/getEventById", { id });
        const ev = res.data;
        this.form = { ...this.form, ...ev };
        this.selectedCourse = ev.course;
        // Initialize application method label/placeholder
        this.onApplicationMethodChange();

        // If title/description are empty, default them from course
        if ((!this.form.title_override || this.form.title_override.trim() === '') && this.selectedCourse) {
          this.form.title_override = this.selectedCourse.title || '';
        }
        if ((!this.form.description_override || this.form.description_override.trim() === '') && this.selectedCourse) {
          this.form.description_override = this.selectedCourse.summary || '';
        }

        // Load existing audience
        this.form.company_ids = ev.companies?.map((c) => c.id) || [];
        this.form.working_group_ids =
          (ev.working_groups || ev.workingGroups)?.map((wg) => wg.id) || [];
        this.form.role_ids = ev.roles?.map((r) => r.id) || [];
        const sessionsRes = await store.dispatch("events/listSessions", { id });
        this.sessions = sessionsRes.data.map((s) => ({
          id: s.id,
          sequence_no: s.sequence_no,
          start_at: s.start_at
            ? s.start_at.replace(" ", "T").substring(0, 16)
            : "",
          end_at: s.end_at ? s.end_at.replace(" ", "T").substring(0, 16) : "",
          venue_id: (s.venue && s.venue.id) || s.venue_id,
          meeting_url: s.meeting_url,
          meeting_passcode: s.meeting_passcode,
          primary_trainer_id:
            s.primary_trainer_id || (s.primaryTrainer && s.primaryTrainer.id),
        }));
        if (this.sessions.length === 0) {
          this.addSession();
        }
        await this.loadMaterials();
        const appsRes = await store.dispatch("events/listApplications", { id });
        this.admin_user_ids = (ev.admins || []).map((u) => u.id);

        // Load existing reminders
        this.reminders = ev.event_reminders || [];

        // Format application_deadline for date input (YYYY-MM-DD)
        if (ev.application_deadline) {
          const date = new Date(ev.application_deadline);
          this.form.application_deadline = date.toISOString().split("T")[0];
        }

        this.loaded = true;
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Failed to load event",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        this.$router.push({ name: "events" });
      } finally {
        this.loading = false;
      }
    },
    getPartTitle(idx) {
      const title =
        this.form.title_override ||
        (this.selectedCourse ? this.selectedCourse.title : "");
      if (this.sessions.length <= 1) {
        return title || "Event timing";
      }
      return title ? `${title} - Part ${idx + 1}` : `Part ${idx + 1}`;
    },
    async loadMaterials() {
      const id = this.$route.params.id;
      try {
        const res = await store.dispatch("events/listMaterials", { id });
        this.materials = (res.data || []).map((m) => ({ ...m }));
      } catch (e) {
        /* silent */
      }
    },
    addSession() {
      this.sessions.push({
        sequence_no: this.sessions.length + 1,
        start_at: "",
        end_at: "",
        venue_id: null,
        meeting_url: "",
        meeting_passcode: "",
        primary_trainer_id: null,
      });
    },
    async saveSession(s, showToast = true) {
      // Skip sessions with empty required fields
      if (!s.start_at || !s.end_at) {
        console.log("Skipping session with empty dates:", s);
        return;
      }

      // Validation is now done before calling this method

      const data = {
        sequence_no: s.sequence_no,
        start_at: s.start_at ? s.start_at.replace("T", " ") + ":00" : null,
        end_at: s.end_at ? s.end_at.replace("T", " ") + ":00" : null,
        venue_id: s.venue_id,
        meeting_url: s.meeting_url,
        meeting_passcode: s.meeting_passcode,
        primary_trainer_id: s.primary_trainer_id,
      };

      if (s.id) {
        await store.dispatch("events/updateSession", { sessionId: s.id, data });
        if (showToast) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Session updated",
              icon: "ThumbsUpIcon",
              variant: "success",
            },
          });
        }
      } else {
        const res = await store.dispatch("events/addSession", {
          id: this.$route.params.id,
          data,
        });
        s.id = res.data.id;
        if (showToast) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Session added",
              icon: "ThumbsUpIcon",
              variant: "success",
            },
          });
        }
      }
    },
    async deleteSession(s) {
      if (!s.id) {
        this.sessions = this.sessions.filter((x) => x !== s);
        return;
      }
      const confirm = await this.$bvModal.msgBoxConfirm(
        "Delete this session?",
        {
          size: "sm",
          title: "Confirm",
          okVariant: "primary",
          okTitle: "Yes",
          cancelTitle: "No",
          cancelVariant: "outline-secondary",
          centered: true,
        }
      );
      if (!confirm) return;
      await store.dispatch("events/deleteSession", { sessionId: s.id });
      this.sessions = this.sessions.filter((x) => x.id !== s.id);
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Session deleted",
          icon: "ThumbsUpIcon",
          variant: "success",
        },
      });
    },
    async saveAdmins() {
      const id = this.$route.params.id;
      await store.dispatch("events/setEventAdmins", {
        id,
        admin_user_ids: this.admin_user_ids,
      });
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Event coordinators updated",
          icon: "ThumbsUpIcon",
          variant: "success",
        },
      });
    },
    async onSubmit() {
      try {
        // Validate required fields
        if (!this.form.course_id) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please select a course",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        if (!this.form.year) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please enter a year",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        if (!this.form.time_zone) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please enter a time zone",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        if (
          !this.form.title_override ||
          this.form.title_override.trim() === ""
        ) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Validation Error",
              text: "Please enter a title",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }

        // Validate application description for non-calendar methods
        if (
          this.form.application_method &&
          this.form.application_method.toLowerCase() !== "calendar"
        ) {
          if (!this.form.application_description) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Validation Error",
                text: "Please provide application details",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
            return;
          }
          if (
            this.form.application_method.toLowerCase() === "email" &&
            !/^([^\s@]+)@([^\s@]+)\.[^\s@]+$/.test(
              this.form.application_description.trim()
            )
          ) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Validation Error",
                text: "Please enter a valid application email address",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
            return;
          }
          if (
            this.form.application_method.toLowerCase() === "external" &&
            !/^(https?:\/\/)?([\w.-]+)\.([a-z\.]{2,6})([\/\w .-]*)*\/?$/i.test(
              this.form.application_description.trim()
            )
          ) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Validation Error",
                text: "Please enter a valid application website URL",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
            return;
          }
        }

        // Validate all sessions before saving anything
        for (const s of this.sessions) {
          // Skip sessions with empty required fields
          if (!s.start_at || !s.end_at) {
            continue;
          }

          // Validate that end_at is after start_at
          if (new Date(s.end_at) <= new Date(s.start_at)) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Session Date Error",
                text: `Part ${s.sequence_no}: End time must be after start time`,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
            return; // Stop here, don't save anything
          }
        }

        this.loading = true;
        const id = this.$route.params.id;

        // Update event
        await store.dispatch("events/updateEvent", {
          id,
          data: { ...this.form, reminders: this.reminders },
        });

        // Update all sessions
        for (const s of this.sessions) {
          await this.saveSession(s, false);
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Event updated",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.$router.push({ name: "events" });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Update failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onApplicationMethodChange() {
      const method = (this.form.application_method || "").toLowerCase();
      if (method && method !== "calendar") {
        if (method === "email") {
          this.applicationDescriptionLabel = "Application Email Address *";
          this.applicationDescriptionPlaceholder = "e.g. training@domain.com";
        } else if (method === "external") {
          this.applicationDescriptionLabel = "Application Website URL *";
          this.applicationDescriptionPlaceholder =
            "https://www.example.com/apply";
        } else {
          this.applicationDescriptionLabel = "Application Details *";
          this.applicationDescriptionPlaceholder =
            "Provide application instructions...";
        }
        this.showApplicationDescription = true;
      } else {
        this.applicationDescriptionLabel = "";
        this.applicationDescriptionPlaceholder = "";
        this.form.application_description = "";
        this.showApplicationDescription = false;
      }
    },
    async uploadMaterial() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        // Validate visible date order
        if (this.materialForm.visible_from && this.materialForm.visible_to) {
          const from = new Date(this.materialForm.visible_from);
          const to = new Date(this.materialForm.visible_to);
          if (to < from) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Material Date Error',
                text: 'Visible to date must be on or after Visible from date',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            });
            this.loading = false;
            return;
          }
        }
        const fd = new FormData();
        fd.append("title", this.materialForm.title || "");
        if (this.materialForm.file) {
          fd.append("file", this.materialForm.file);
        }
        if (this.materialForm.external_url) {
          fd.append("external_url", this.materialForm.external_url);
        }
        if (this.materialForm.visible_from) {
          fd.append("visible_from", this.materialForm.visible_from);
        }
        if (this.materialForm.visible_to) {
          fd.append("visible_to", this.materialForm.visible_to);
        }
        fd.append(
          "is_visible_to_accepted_only",
          this.materialForm.is_visible_to_accepted_only ? 1 : 0
        );
        if (this.materialForm.course_id) {
          fd.append("course_id", this.materialForm.course_id);
        }
        const res = await store.dispatch("events/uploadMaterial", {
          id,
          data: fd,
        });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Material uploaded",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.materialForm = {
          title: "",
          file: null,
          external_url: "",
          visible_from: "",
          visible_to: "",
          is_visible_to_accepted_only: true,
          course_id: null,
        };
        await this.loadMaterials();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Upload failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    editMaterialStart(m) {
      this.editingId = m.id;
      this.editRow = {
        title: m.title,
        external_url: m.external_url,
        visible_from: m.visible_from,
        visible_to: m.visible_to,
        is_visible_to_accepted_only: !!m.is_visible_to_accepted_only,
        course_id: m.course_id || null,
        file: null,
      };
    },
    editMaterialCancel() {
      this.editingId = null;
      this.editRow = {
        title: "",
        external_url: "",
        visible_from: "",
        visible_to: "",
        is_visible_to_accepted_only: true,
        course_id: null,
        file: null,
      };
    },
    async editMaterialSave(m) {
      try {
        this.loading = true;
        // Validate visible date order
        if (this.editRow.visible_from && this.editRow.visible_to) {
          const from = new Date(this.editRow.visible_from);
          const to = new Date(this.editRow.visible_to);
          if (to < from) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Material Date Error',
                text: 'Visible to date must be on or after Visible from date',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            });
            this.loading = false;
            return;
          }
        }
        const fd = new FormData();
        if (this.editRow.title) {
          fd.append("title", this.editRow.title);
        }
        if (this.editRow.external_url) {
          fd.append("external_url", this.editRow.external_url);
        }
        if (this.editRow.visible_from) {
          fd.append("visible_from", this.editRow.visible_from);
        }
        if (this.editRow.visible_to) {
          fd.append("visible_to", this.editRow.visible_to);
        }
        if (typeof this.editRow.is_visible_to_accepted_only !== "undefined") {
          fd.append(
            "is_visible_to_accepted_only",
            this.editRow.is_visible_to_accepted_only ? 1 : 0
          );
        }
        if (this.editRow.course_id) {
          fd.append("course_id", this.editRow.course_id);
        }
        if (this.editRow.file) {
          fd.append("file", this.editRow.file);
        }
        await store.dispatch("events/updateMaterial", {
          materialId: m.id,
          data: fd,
        });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Material updated",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.editMaterialCancel();
        await this.loadMaterials();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Update failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async deleteMaterial(m) {
      const ok = await this.$bvModal.msgBoxConfirm("Delete this material?", {
        size: "sm",
        title: "Confirm",
        okVariant: "primary",
        okTitle: "Yes",
        cancelTitle: "No",
        cancelVariant: "outline-secondary",
        centered: true,
      });
      if (!ok) return;
      try {
        this.loading = true;
        await store.dispatch("events/deleteMaterial", { materialId: m.id });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Material deleted",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        await this.loadMaterials();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Delete failed",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
    // Modal methods
    openAddVenueModal(sessionIndex) {
      this.currentSessionIndex = sessionIndex;
      this.showAddVenueModal = true;
    },
    openAddTrainerModal(sessionIndex) {
      this.currentSessionIndex = sessionIndex;
      this.showAddTrainerModal = true;
    },
    async handleAddVenue(bvModalEvent) {
      bvModalEvent.preventDefault();
      if (!this.newVenue.name.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please fill venue name",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      try {
        const response = await store.dispatch(
          "settings/createVenue",
          this.newVenue
        );
        const newVenue = response.data.data;
        this.options.venues.push(newVenue);

        // Auto-select the newly added venue in the current session
        if (
          this.currentSessionIndex !== null &&
          this.sessions[this.currentSessionIndex]
        ) {
          this.sessions[this.currentSessionIndex].venue_id = newVenue.id;
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Venue added successfully",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.resetVenueForm();
        this.showAddVenueModal = false;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error adding venue",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    async handleAddTrainer(bvModalEvent) {
      bvModalEvent.preventDefault();
      if (!this.newTrainer.name.trim() || !this.newTrainer.email.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please fill all required trainer fields",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      // Email format validation
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
      if (!emailRegex.test(this.newTrainer.email.trim())) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please enter a valid email address",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append("name", this.newTrainer.name.trim());
        formData.append("email", this.newTrainer.email.trim());
        if (this.newTrainer.phone.trim()) {
          formData.append("phone", this.newTrainer.phone.trim());
        }
        if (this.newTrainerCV) {
          formData.append("cv_pdf", this.newTrainerCV);
        }

        // Add to options via API call
        const response = await store.dispatch(
          "settings/createTrainerWithFile",
          formData
        );
        const newTrainer = response.data.data;
        this.options.trainers.push(newTrainer);

        // Auto-select the newly added trainer in the current session
        if (
          this.currentSessionIndex !== null &&
          this.sessions[this.currentSessionIndex]
        ) {
          this.sessions[this.currentSessionIndex].primary_trainer_id =
            newTrainer.id;
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Trainer added successfully",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
        this.resetTrainerForm();
        this.showAddTrainerModal = false;
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error adding trainer",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
    resetVenueForm() {
      this.newVenue = {
        name: "",
      };
    },
    resetTrainerForm() {
      this.newTrainer = {
        name: "",
        email: "",
        phone: "",
      };
      this.newTrainerCV = null;
    },
    // Reminder Methods
    addReminder() {
      this.reminders.push({
        days_before: 3,
        message: "",
        type: "event_reminder",
        is_active: true,
      });
    },
    removeReminder(index) {
      this.reminders.splice(index, 1);
    },
    materialFileHref(path) {
      const base = this.materialsURL || '';
      return base.replace(/\/$/, '') + '/' + String(path || '').replace(/^\//, '');
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.section-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
}

.parts-section {
  background: #f5f8ff;
  border: 1px solid #dbe6ff;
  border-radius: 0.5rem;
  padding: 1rem;
}

.parts-header {
  padding-bottom: 0.5rem;
  border-bottom: 1px dashed #cdd9ff;
  margin-bottom: 0.75rem;
}

.session-card-edit {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.session-edit-header {
  padding-bottom: 0.25rem;
  border-bottom: 1px dashed #e9ecef;
}
</style>
